import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';
import {
  LayoutDashboard, Route, Truck, FileText, Clock, Trophy,
  Users, Car, MapPin, Receipt, CreditCard,
  BarChart3,
  Settings, LogOut, Menu, X, ChevronDown, ChevronRight,
} from 'lucide-react';

// ========== Logo URL ==========

const LOGO_URL =
  'https://lrzcturtxtzhdzqacefy.supabase.co/storage/v1/object/public/Imagenes/ISO,%20NEG@2x.png';

// ========== Menu Config ==========

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface MenuSection {
  role: string;
  title: string;
  items: MenuItem[];
}

const SECTIONS: MenuSection[] = [
  {
    role: 'driver',
    title: 'Conductor',
    items: [
      { label: 'Dashboard', path: '/app/driver/dashboard', icon: <LayoutDashboard size={18} /> },
      { label: 'Mis Rutas', path: '/app/driver/dashboard', icon: <Route size={18} /> },
      { label: 'Vehículos', path: '/app/driver/vehicles', icon: <Truck size={18} /> },
      { label: 'Documentos', path: '/app/driver/documents', icon: <FileText size={18} /> },
      { label: 'Disponibilidad', path: '/app/driver/availability', icon: <Clock size={18} /> },
      { label: 'Earnings', path: '/app/driver/gamification', icon: <Trophy size={18} /> },
    ],
  },
  {
    role: 'fleet_manager',
    title: 'Mi Flota',
    items: [
      { label: 'Dashboard', path: '/app/fleet/dashboard', icon: <LayoutDashboard size={18} /> },
      { label: 'Conductores', path: '/app/fleet/drivers', icon: <Users size={18} /> },
      { label: 'Vehículos', path: '/app/fleet/vehicles', icon: <Car size={18} /> },
      { label: 'Trips', path: '/app/fleet/trips', icon: <MapPin size={18} /> },
      { label: 'Facturación', path: '/app/fleet/invoicing', icon: <Receipt size={18} /> },
      { label: 'Documentos', path: '/app/fleet/documents', icon: <FileText size={18} /> },
      { label: 'Pagos', path: '/app/fleet/invoices', icon: <CreditCard size={18} /> },
    ],
  },
  {
    role: 'customer',
    title: 'Cliente',
    items: [
      { label: 'Dashboard', path: '/app/customer/dashboard', icon: <LayoutDashboard size={18} /> },
      { label: 'Rutas', path: '/app/customer/routes', icon: <Route size={18} /> },
      { label: 'Billing', path: '/app/customer/billing', icon: <CreditCard size={18} /> },
      { label: 'Reportes', path: '/app/customer/billing', icon: <BarChart3 size={18} /> },
    ],
  },
];

// ========== Component ==========

export function UnifiedSidebar() {
  const { user, logout, hasRole } = useUnifiedAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const visibleSections = user?.godMode ? SECTIONS : SECTIONS.filter(s => hasRole(s.role));

  const toggleSection = (role: string) => {
    setCollapsed(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const navLinkClass = (path: string) => {
    const active = location.pathname === path;
    return `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
      active
        ? 'bg-white text-partrunner-black font-medium shadow-card'
        : 'text-partrunner-black/80 hover:bg-white/50'
    }`;
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-black/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center overflow-hidden">
            <img
              src={LOGO_URL}
              alt="Partrunner"
              className="w-7 h-7 object-contain"
            />
          </div>
          <span className="text-xl font-extrabold text-partrunner-black uppercase tracking-tight">
            Partrunner
          </span>
        </div>
      </div>

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {visibleSections.map((section, idx) => {
          const isCollapsed = collapsed[section.role];
          return (
            <div key={section.role}>
              {idx > 0 && <div className="my-2 border-t border-black/10" />}
              <button
                onClick={() => toggleSection(section.role)}
                className="flex items-center justify-between w-full px-4 pt-1 pb-1 text-[10px] font-semibold text-partrunner-black/50 uppercase tracking-wider hover:text-partrunner-black/70 transition"
              >
                <span>{section.title}</span>
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </button>
              {!isCollapsed && (
                <div className="mt-1 space-y-0.5">
                  {section.items.map(item => (
                    <NavLink
                      key={item.path + item.label}
                      to={item.path}
                      className={navLinkClass(item.path)}
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-black/10 px-4 py-3 space-y-1">
        <NavLink to="/app/profile" className={navLinkClass('/app/profile')} onClick={() => setMobileOpen(false)}>
          <Settings size={18} />
          Perfil
        </NavLink>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-partrunner-black/60 hover:text-partrunner-black hover:bg-white/50 transition-all duration-200 w-full"
        >
          <LogOut size={18} />
          Cerrar Sesión
        </button>
      </div>

      {/* User info */}
      {user && (
        <div className="px-4 py-3 border-t border-black/10">
          <p className="text-partrunner-black text-sm font-medium truncate">{user.displayName || user.email || user.phone}</p>
          <p className="text-partrunner-black/50 text-xs truncate">{user.email || user.phone}</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-white rounded-xl border border-partrunner-gray-light text-partrunner-black shadow-card"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-partrunner-yellow">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-partrunner-black/60 hover:text-partrunner-black"
            >
              <X size={20} />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-partrunner-yellow border-r border-black/10 min-h-screen">
        {sidebarContent}
      </aside>
    </>
  );
}
